import { Component, OnInit, OnDestroy, ViewChild, ElementRef, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { BlogService } from '../../../_services/api/blog.service';
import { BlogDetail, BlogCreate, BlogUpdate } from '../../../_models/blogs/blog.model';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';

import { QuillEditorComponent } from 'ngx-quill';

@Component({
  selector: 'app-admin-blog-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, QuillEditorComponent],
  templateUrl: './admin-blog-form.component.html',
  styleUrls: ['./admin-blog-form.component.scss']
})
export class AdminBlogFormComponent implements OnInit, OnDestroy {
  blogForm: FormGroup;
  isEditMode = false;
  blogId?: number;
  isLoading = false;
  isSubmitting = false;
  showSuccess = false;
  successMessage = '';
  slugAlreadyExists = false;
  
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  existingMainFile?: { id: number, location: string };
  
  editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],               // custom button values
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
      [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
      [{ 'direction': 'rtl' }],                         // text direction
      [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],                                         // remove formatting button
      ['link', 'image', 'video']                         // link and image/video
    ]
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private blogService: BlogService,
    private route: ActivatedRoute,
    private router: Router,
    private notification: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.blogForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(150)]],
      slug: [''],
      content: ['', Validators.required],
      excerpt: ['', Validators.maxLength(200)],
      visible: [false]
    });
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.blogId = +params['id'];
        this.loadBlogDetails();
      }
    });

    this.blogForm.get('title')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(title => {
      if (!this.isEditMode && title && !this.blogForm.get('slug')?.touched) {
        const slug = this.generateSlug(title);
        this.blogForm.get('slug')?.setValue(slug, { emitEvent: false });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setVisibility(isVisible: boolean): void {
    this.blogForm.get('visible')?.setValue(isVisible);
  }

  private loadBlogDetails(): void {
    if (!this.blogId) return;
    
    this.isLoading = true;
    this.blogService.getBlogDetails(this.blogId).subscribe({
      next: (response: ApiResponse<BlogDetail>) => {
        const blog = response.data;
        this.blogForm.patchValue({
          title: blog.title,
          slug: blog.slug,
          content: blog.content,
          excerpt: blog.excerpt,
          visible: blog.visible
        });

        const mainFile = blog.blogFiles?.find(file => file.isMain);
        if (mainFile) {
          this.existingMainFile = {
            id: mainFile.id,
            location: mainFile.location
          };
          this.previewUrl = mainFile.location;
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading blog:', error);
        this.notification.showError('Erreur lors du chargement de l\'article');
        this.isLoading = false;
        this.router.navigate(['/admin/blogs']);
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      if (!file.type.startsWith('image/')) {
        this.notification.showError('Veuillez sélectionner une image valide');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.notification.showError('L\'image ne doit pas dépasser 5MB');
        return;
      }
      this.selectedFile = file;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.previewUrl = null;
  }

  removeExistingFile(): void {
    if (this.blogId && this.existingMainFile) {
      const filesToDelete = [this.existingMainFile.id];
      this.blogForm.get('filesToDelete')?.setValue(filesToDelete);
      this.existingMainFile = undefined;
      this.previewUrl = null;
    }
  }

  onSubmit(): void {
    this.slugAlreadyExists = false;
    if (this.blogForm.invalid) {
      this.markFormGroupTouched(this.blogForm);
      return;
    }

    this.isSubmitting = true;
    
    if (this.isEditMode && this.blogId) {
      this.updateBlog();
    } else {
      this.createBlog();
    }
  }

  private createBlog(): void {
    const blogData: BlogCreate = {
      title: this.blogForm.get('title')?.value,
      slug: this.blogForm.get('slug')?.value || undefined,
      content: this.blogForm.get('content')?.value,
      excerpt: this.blogForm.get('excerpt')?.value || undefined,
      visible: this.blogForm.get('visible')?.value
    };

    this.blogService.createBlog(blogData, this.selectedFile || undefined)
      .subscribe({
        next: (response: ApiResponse<BlogDetail>) => {
          
          this.showSuccess = true;
          this.successMessage = 'Article créé avec succès!';
          this.notification.showSuccess('Article créé avec succès');
          this.resetForm();         
           setTimeout(() => {
            this.isSubmitting = false;
            //this.router.navigate(['/admin/articles/details', response.data.id]);
            if (isPlatformBrowser(this.platformId)) {
              window.scrollTo({top: 0, behavior: 'smooth'});
            }
          }, 500);
        },
        error: (error) => {
          console.error('Error creating blog:', error);
          this.isSubmitting = false;
          
          if (error.status === 409 || error.status === 403) {
            this.slugAlreadyExists = true;
            this.notification.showError('Ce slug est déjà utilisé');
            this.scrollToInput();
          } else {
            this.notification.showError('Erreur lors de la création de l\'article');
          }
        }
      });
  }

  private updateBlog(): void {
    if (!this.blogId) return;

    const blogData: BlogUpdate = {
      title: this.blogForm.get('title')?.value,
      slug: this.blogForm.get('slug')?.value || undefined,
      content: this.blogForm.get('content')?.value,
      excerpt: this.blogForm.get('excerpt')?.value || undefined,
      visible: this.blogForm.get('visible')?.value
    };

    if (this.blogForm.get('filesToDelete')?.value) {
      blogData.filesToDelete = this.blogForm.get('filesToDelete')?.value;
    }

    if (this.existingMainFile && !this.selectedFile) {
      blogData.mainFileId = this.existingMainFile.id;
    }

    this.blogService.updateBlog(this.blogId, blogData, this.selectedFile || undefined)
      .subscribe({
        next: (response: ApiResponse<BlogDetail>) => {
          
          this.successMessage = 'Article modifié avec succès!';
          this.resetForm();

          setTimeout(() => {
            this.isSubmitting = false;
            this.showSuccess = true;
            this.notification.showSuccess('Article modifié avec succès');
            //this.router.navigate(['/admin/articles/details', response.data.id]);
            if (isPlatformBrowser(this.platformId)) {
              window.scrollTo({top: 0, behavior: 'smooth'});
            }
          }, 500);
        },
        error: (error) => {
          console.error('Error updating blog:', error);
          this.isSubmitting = false;
          
          if (error.status === 409 || error.status === 403) {
            this.slugAlreadyExists = true;
            this.notification.showError('Ce slug est déjà utilisé');
            this.scrollToInput();
          } else {
            this.notification.showError('Erreur lors de la modification de l\'article');
          }
        }
      });
  }

  private resetForm(): void {
    this.blogForm.reset({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      visible: false
    });
    this.selectedFile = null;
    this.previewUrl = null;
    this.existingMainFile = undefined;
    this.blogForm.markAsPristine();
    this.blogForm.markAsUntouched();
  }

  createAnother(): void {
    this.showSuccess = false;
  }

  goToList(): void {
    this.router.navigate(['/admin/articles']);
  }

  @ViewChild('slugInput') slugInput!: ElementRef;
  scrollToInput() {
    if (this.slugInput) {
      this.slugInput.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        this.slugInput.nativeElement.focus();
      }, 200);
    }
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get title() { return this.blogForm.get('title'); }
  get slug() { return this.blogForm.get('slug'); }
  get content() { return this.blogForm.get('content'); }
  get excerpt() { return this.blogForm.get('excerpt'); }
  get visible() { return this.blogForm.get('visible'); }
}